import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import useGlobalMutation from "../../../hooks/useGlobalMutation";
import {
    COUNTRY_LIST,
    SITE_ADMIN_APP_LIST,
    Helper,
    clearCache,
    RegexPattern,
} from "../../../utils";
import { FormContainer, CountrySelector, TextInput } from "../../shared";
import { UPDATE_PERMISSIONS } from "./query";

const PermissionManager = ({ id, appPermissions, countryPermissions }) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
        register,
        watch,
        setValue,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            countryPermissions: countryPermissions.map((country) => ({ country })),
            appPermissions: appPermissions
                .filter((app) => app !== "auth_public")
                .map((appName) => ({ appName })),
        },
    });

    const countryAvailablity = watch("countryAvailablity");
    const countryName = watch("countryName");

    const {
        fields: countryFields,
        append: appendCountry,
        remove: removeCountry,
    } = useFieldArray({
        control,
        name: "countryPermissions",
    });

    const {
        fields: appFields,
        append: appendApp,
        remove: removeApp,
    } = useFieldArray({
        control,
        name: "appPermissions",
    });

    const handleAddCountry = (country) => {
        const countryList = countryFields.map(({ country }) => country);
        if (!countryList.includes(country)) {
            appendCountry({ country });
        }
    };
    const handleAddCustomCountry = (e) => {
        e.preventDefault();
        if (countryName.length > 0) {
            const country = countryName.toUpperCase();
            const countryList = countryFields.map(({ country }) => country);

            if (!countryList.includes(country)) {
                appendCountry({ country });
            }
            setValue("countryName", "");
        } else {
            setError("countryName");
        }
    };

    const handleAddApp = (app) => {
        const appList = appFields.map(({ appName }) => appName);

        if (appList.includes(app)) {
            removeApp(appFields.findIndex((item) => item.appName === app));
        } else {
            appendApp({ appName: app });
        }
    };

    useEffect(() => {
        if (countryFields.length === 0) {
            setError(
                "countryPermissions",
                { type: "required" },
                "Please select at least one country"
            );
        } else {
            clearErrors("countryPermissions");
        }

        if (appFields.length === 0) {
            setError(
                "appPermissions",
                { type: "required" },
                "Please select at least one Application"
            );
        } else {
            clearErrors("appPermissions");
        }
    }, [countryFields, appFields]);

    const { submitMutation, loading } = useGlobalMutation(UPDATE_PERMISSIONS, (cache) => {
        clearCache(cache, "getAdminUserList");
    });

    const submitAction = handleSubmit(async (data) => {
        const countryPermissions = data.countryPermissions.map(({ country }) => country);
        const adminPermissions = data.appPermissions.map(({ appName }) => appName);

        const variables = {
            userId: id,
            countryPermissions,
            adminPermissions,
            userPermissions: [],
            readPermissions: [],
            basicAuthPermissions: [],
        };
        const dataPath = "updatePermissionsById.permissions";
        const successMessage = "Permissions Update Successfully";
        await submitMutation(variables, dataPath, successMessage);
    });

    return (
        <FormContainer
            submitAction={submitAction}
            submitBtnSm
            submitButtonText="Update Permission"
            loading={loading}
        >
            <h5 className="mb-4">Permission Manager</h5>

            <div className="card mb-4">
                <div className="card-body">
                    <h6>Country Permission</h6>

                    <CountrySelector
                        id="country"
                        name="country"
                        selectCountry={(val) => handleAddCountry(val)}
                        value={""}
                        valueType="short"
                        isInvalid={!!errors.countryPermissions}
                        errorMessage="Please select at least one country"
                        blacklist={countryFields.map(({ country }) => country)}
                    />

                    <label className="mb-4" htmlFor="countryAvailablity">
                        <input
                            className="mr-2"
                            type="checkbox"
                            id="countryAvailablity"
                            {...register("countryAvailablity", {})}
                        />
                        Can&apos;t find your country?
                    </label>

                    {countryAvailablity && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <TextInput
                                    id="countryName"
                                    labelText="Country Name"
                                    {...register("countryName", {
                                        pattern: RegexPattern.nameWithoutSpace,
                                    })}
                                    isInvalid={!!errors.countryName}
                                />
                                <button
                                    onClick={(e) => {
                                        handleAddCustomCountry(e);
                                    }}
                                    type="btn"
                                    className="btn btn-primary"
                                >
                                    Add Country
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="card">
                        <div className="card-body">
                            <h6>Selected Countries:</h6>
                            <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                                {countryFields.map(({ id, country }, index) => (
                                    <div
                                        className="alert--customize px-3 d-flex align-items-center mr-0"
                                        key={id}
                                    >
                                        {COUNTRY_LIST[country] || country}

                                        <button
                                            type="button"
                                            className="close ml-2 p-0"
                                            onClick={() => removeCountry(index)}
                                            aria-label="Close"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <h6>Application Permission</h6>

                    {SITE_ADMIN_APP_LIST.map(({ id }) => (
                        <div className="list-group-item list-group-item-action" key={id}>
                            <label
                                className="customcheck p-0 d-flex justify-content-between mb-0"
                                style={{ fontSize: "16px" }}
                                htmlFor={`appPermissions-${id}`}
                            >
                                {Helper.getAppNameById(id)}

                                <div className="position-relative" style={{ width: "25px" }}>
                                    <input
                                        id={`appPermissions-${id}`}
                                        type="checkbox"
                                        name="basicLoginDomains"
                                        value={id}
                                        checked={appFields.map((item) => item.appName).includes(id)}
                                        onChange={() => handleAddApp(id)}
                                    />
                                    <span className="checkmark"></span>
                                </div>
                            </label>
                        </div>
                    ))}

                    {errors.appPermissions && (
                        <div className="invalid-feedback d-block">
                            Please select at least one Application
                        </div>
                    )}
                </div>
            </div>
        </FormContainer>
    );
};

export default PermissionManager;
